/**
 * Review Response - full page.
 *
 * Reached from the prompt shown after a response is generated with Review
 * Mode on. The response is held back until the reviewer decides here:
 *
 *   Cancel Review             -> save the response exactly as generated
 *   Confirm Reviewed Response -> save the reviewed response
 *                                (disabled until every point is checked)
 *
 * Cancel is the same decision as declining the review at the prompt, so both
 * call the same endpoint. Confirm is enforced by the server as well, so the
 * rule does not rest on the button being disabled.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Empty,
  Input,
  Modal,
  Progress,
  Spin,
  Tooltip,
  message as antMessage,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  ExclamationCircleFilled,
  FileTextOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";

import { REVIEW } from "../../api/endpointMap";
import { fetchData, postData, updateData } from "../../api/apiService";

const CHIP = {
  fact: { label: "S", tone: "fact", title: "Fact" },
  figure: { label: "₹", tone: "figure", title: "Figure" },
  citation: { label: "C", tone: "citation", title: "Citation" },
  unsourced: { label: "!", tone: "unsourced", title: "No source found" },
};

const STYLES = `
.rm-body { line-height: 1.9; }
.rm-body .rm-tag {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 4px; margin: 0 3px;
  border-radius: 9px; font-size: 11px; font-weight: 700; line-height: 1;
  cursor: pointer; vertical-align: middle; user-select: none;
  border: 1px solid transparent; transition: transform .08s ease;
}
.rm-body .rm-tag:hover { transform: scale(1.15); }
.rm-body .rm-tag.rm-fact { background:#e6f0ff; color:#1d4ed8; border-color:#bfdbfe; }
.rm-body .rm-tag.rm-figure { background:#fef3c7; color:#92400e; border-color:#fde68a; }
.rm-body .rm-tag.rm-citation { background:#ede9fe; color:#6d28d9; border-color:#ddd6fe; }
.rm-body .rm-tag.rm-unsourced { background:#fee2e2; color:#b91c1c; border-color:#fecaca; }
.rm-body .rm-tag.rm-confirmed { box-shadow: 0 0 0 2px #86efac; }
.rm-body .rm-tag.rm-active { box-shadow: 0 0 0 2px #64748b; }
`;

export default function ReviewResponsePage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = searchParams.get("returnTo");

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [busyTagId, setBusyTagId] = useState(null);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addAnchor, setAddAnchor] = useState("");
  const [addText, setAddText] = useState("");

  const bodyRef = useRef(null);

  const tagsById = useMemo(() => {
    const map = {};
    (session?.tags || []).forEach((t) => {
      map[t.tag_id] = t;
    });
    return map;
  }, [session]);

  const selectedTag = selectedTagId ? tagsById[selectedTagId] : null;
  const unsourcedCount = useMemo(
    () => (session?.tags || []).filter((t) => t.unsourced).length,
    [session],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchData(REVIEW.SESSION(sessionId));
      setSession(data);
    } catch (e) {
      antMessage.error("Could not open this review");
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  // Decorate the markers inside the generated HTML. Imperative because the
  // markers sit at arbitrary depths: splitting the HTML to render React nodes
  // would break whichever element a marker sits inside.
  useEffect(() => {
    const root = bodyRef.current;
    if (!root || !session) return undefined;

    const nodes = root.querySelectorAll("span.rm-tag");
    const cleanups = [];

    nodes.forEach((node) => {
      const id = node.getAttribute("data-tag-id");
      const tag = tagsById[id];
      if (!tag) return;
      const chip = CHIP[tag.kind] || CHIP.fact;
      node.textContent = chip.label;
      node.setAttribute("title", tag.doc || chip.title);
      node.className = [
        "rm-tag",
        `rm-${chip.tone}`,
        tag.confirmed ? "rm-confirmed" : "",
        id === selectedTagId ? "rm-active" : "",
      ]
        .filter(Boolean)
        .join(" ");

      const onClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setSelectedTagId(id);
      };
      node.addEventListener("click", onClick);
      cleanups.push(() => node.removeEventListener("click", onClick));
    });

    return () => cleanups.forEach((fn) => fn());
  }, [session, tagsById, selectedTagId]);

  // Active-review time, only while an unfinished review is open.
  useEffect(() => {
    if (!session?.session_id || session.review_status !== "in_review") {
      return undefined;
    }
    const id = setInterval(() => {
      postData(REVIEW.HEARTBEAT(session.session_id), { seconds: 30 }).catch(
        () => {},
      );
    }, 30000);
    return () => clearInterval(id);
  }, [session?.session_id, session?.review_status]);

  const goBack = () => {
    if (returnTo) navigate(returnTo, { replace: true });
    else navigate(-1);
  };

  const handleSkip = async () => {
    setResolving(true);
    try {
      await postData(REVIEW.SKIP(sessionId), {});
      antMessage.success("Response saved without review");
      goBack();
    } catch (e) {
      antMessage.error(typeof e === "string" ? e : "Could not save the response");
      setResolving(false);
    }
  };

  const handleConfirm = async () => {
    setResolving(true);
    try {
      await postData(REVIEW.FINALISE(sessionId), {});
      antMessage.success("Reviewed response saved");
      goBack();
    } catch (e) {
      antMessage.error(
        typeof e === "string" ? e : e?.detail || "Check every point before confirming",
      );
      setResolving(false);
    }
  };

  const handleTagConfirm = async (tagId, confirmed) => {
    setBusyTagId(tagId);
    try {
      const data = await updateData(REVIEW.CONFIRM_TAG(sessionId, tagId), {
        confirmed,
      });
      setSession(data);
    } catch (e) {
      antMessage.error(typeof e === "string" ? e : "Could not update this item");
    } finally {
      setBusyTagId(null);
    }
  };

  const handleAddPoint = async () => {
    if (!addText.trim()) return;
    try {
      const data = await postData(REVIEW.ADD_POINT(sessionId), {
        anchor: addAnchor,
        text_html: addText,
      });
      setSession(data);
      setAddOpen(false);
      setAddAnchor("");
      setAddText("");
      antMessage.success("Point added");
    } catch (e) {
      antMessage.error(typeof e === "string" ? e : "Could not add the point");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Spin size="large" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-6">
        <Empty description="This review is no longer available" />
        <div className="flex justify-center mt-4">
          <Button onClick={goBack}>Back to the notice</Button>
        </div>
      </div>
    );
  }

  const total = session.total_tags || 0;
  const confirmed = session.confirmed_count || 0;
  const percent = total ? Math.round((confirmed / total) * 100) : 0;
  const canConfirm = Boolean(session.all_mandatory_confirmed) && total > 0;
  const isResolved = session.review_status !== "in_review";

  return (
    <div className="p-4 sm:p-6">
      <style>{STYLES}</style>

      <div className="flex items-center gap-3 mb-4">
        <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
          Back
        </Button>
        <div className="flex items-center gap-2">
          <SafetyCertificateOutlined className="text-blue-600 text-lg" />
          <h2 className="text-xl font-semibold text-gray-800 m-0">
            Review Response
          </h2>
        </div>
        {isResolved && (
          <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-medium">
            Already saved
          </span>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Progress
              percent={percent}
              size="small"
              className="flex-1 mb-0"
              strokeColor={percent === 100 ? "#16a34a" : "#2563eb"}
            />
            <span className="text-sm text-gray-600 whitespace-nowrap">
              {confirmed} of {total} points checked
            </span>
            <Button
              size="small"
              icon={<PlusOutlined />}
              disabled={isResolved}
              onClick={() => setAddOpen(true)}
            >
              Add point
            </Button>
          </div>
          {unsourcedCount > 0 && (
            <div className="mt-2 flex items-center gap-2 text-sm text-red-700">
              <ExclamationCircleFilled />
              <span>
                {unsourcedCount} point{unsourcedCount > 1 ? "s" : ""} could not
                be traced to a source document. Read these carefully.
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3">
          <div className="lg:col-span-2 p-5 max-h-[calc(100vh-330px)] overflow-y-auto">
            <div
              ref={bodyRef}
              className="rm-body prose max-w-none text-sm text-gray-800"
              dangerouslySetInnerHTML={{ __html: session.tagged_html || "" }}
            />
          </div>

          <div className="border-t lg:border-t-0 lg:border-l border-gray-200 p-4 bg-gray-50 max-h-[calc(100vh-330px)] overflow-y-auto">
            {!selectedTag ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Select a marker in the response to see its source"
              />
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  {selectedTag.unsourced ? (
                    <ExclamationCircleFilled className="text-red-600" />
                  ) : (
                    <FileTextOutlined className="text-blue-600" />
                  )}
                  <span className="font-medium text-gray-800 break-all">
                    {selectedTag.unsourced
                      ? "No source found"
                      : selectedTag.doc || "Source"}
                  </span>
                </div>

                {selectedTag.anchor && (
                  <div className="mb-3">
                    <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                      In the response
                    </div>
                    <div className="text-sm text-gray-800 bg-white border border-gray-200 rounded p-2">
                      {selectedTag.anchor}
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Supporting text
                  </div>
                  {selectedTag.snippet ? (
                    <div className="text-sm text-gray-700 bg-white border border-gray-200 rounded p-2 whitespace-pre-wrap">
                      {selectedTag.snippet}
                    </div>
                  ) : (
                    <Alert
                      type="warning"
                      showIcon
                      message="This point was not matched to any supplied document. Verify it yourself before relying on it."
                    />
                  )}
                </div>

                <Button
                  block
                  type={selectedTag.confirmed ? "default" : "primary"}
                  icon={
                    selectedTag.confirmed ? (
                      <CheckCircleFilled className="text-green-600" />
                    ) : (
                      <CheckCircleOutlined />
                    )
                  }
                  loading={busyTagId === selectedTag.tag_id}
                  disabled={isResolved}
                  onClick={() =>
                    handleTagConfirm(selectedTag.tag_id, !selectedTag.confirmed)
                  }
                >
                  {selectedTag.confirmed ? "Checked" : "Mark as checked"}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-white">
          <span className="text-sm text-gray-600">
            {isResolved
              ? "This response has already been saved."
              : "The response is not saved onto the notice until you choose below."}
          </span>
          <div className="flex gap-2">
            <Button
              disabled={resolving || isResolved}
              loading={resolving}
              onClick={handleSkip}
            >
              Cancel Review
            </Button>
            <Tooltip
              title={
                canConfirm || isResolved
                  ? ""
                  : "Check every point in the response to enable this"
              }
            >
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                disabled={resolving || isResolved || !canConfirm}
                loading={resolving}
                onClick={handleConfirm}
              >
                Confirm Reviewed Response
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>

      <Modal
        title="Add a point the draft missed"
        open={addOpen}
        onOk={handleAddPoint}
        onCancel={() => setAddOpen(false)}
        okText="Add point"
        okButtonProps={{ disabled: !addText.trim() }}
      >
        <p className="text-sm text-gray-600 mb-3">
          Your wording is added to the response and marked as needing your own
          verification, because it does not come from a supplied document.
        </p>
        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-1">
            Place it after this phrase (optional)
          </div>
          <Input
            value={addAnchor}
            onChange={(e) => setAddAnchor(e.target.value)}
            placeholder="Leave blank to add at the end"
          />
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Your point</div>
          <Input.TextArea
            rows={4}
            value={addText}
            onChange={(e) => setAddText(e.target.value)}
            placeholder="Type the point to add"
          />
        </div>
      </Modal>
    </div>
  );
}
